import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useServiceBotEnabled } from '@user/store/tenantFeatures/hooks/useServiceBotEnabled'
import {
  LeadType,
  leadTypeImportOption,
  LeadTypeOptions,
  leadTypeServiceBotOption,
} from '@user/components/Leads/LeadFilters/typings'
import { IOption } from '@user/components/Filters'
import {
  type AssignmentRuleDTO,
  selectAllAssignmentRules,
  useGetAssignmentRulesOnMount,
} from '@user/store/leadAssignmentRule'
import {
  getAdditionalRequiredFieldOptions,
  getAutoReviewSettings,
  IAutoReviewAdditionalField,
  IAutoReviewSettingsInput,
  updateAutoReviewSettings,
} from '@user/store/autoReviewSettings'
import { ADDITIONAL_FIELD_NAMES } from '../constants'

const LEAD_TYPE_ATTRIBUTE = 'LeadType'
const LEAD_TYPE_ATTRIBUTE_LOWER = LEAD_TYPE_ATTRIBUTE.toLowerCase()
const importLeadTypeValues = new Set(
  leadTypeImportOption.map((option) => String(option.value)),
)

export type LeadTypeAssignmentRule = {
  assignmentRuleId: number
  assignmentRuleName: string
  types: string[]
}

const sortFieldOptions = (
  options: IAutoReviewAdditionalField[],
): IAutoReviewAdditionalField[] => {
  const rank = (option: IAutoReviewAdditionalField) => {
    const index = ADDITIONAL_FIELD_NAMES.indexOf(option.fieldName)
    return index === -1 ? ADDITIONAL_FIELD_NAMES.length : index
  }
  const builtIns = options
    .filter((option) => !option.isCustomField)
    .sort((a, b) => rank(a) - rank(b) || a.fieldName.localeCompare(b.fieldName))
  const custom = options
    .filter((option) => option.isCustomField)
    .sort((a, b) => a.fieldName.localeCompare(b.fieldName))

  return [...builtIns, ...custom]
}

export const useData = () => {
  const [autoReviewEnabled, setAutoReviewEnabled] = useState<boolean>(false)
  const [selectedTypes, setSelectedTypes] = useState<LeadType[]>([])
  const [requiredAdditionalFields, setRequiredAdditionalFields] = useState<
    IAutoReviewAdditionalField[]
  >([])
  const [fieldOptions, setFieldOptions] = useState<
    IAutoReviewAdditionalField[]
  >([])
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const enabledServiceBot = useServiceBotEnabled()
  const { loading: assignmentRulesLoading } = useGetAssignmentRulesOnMount()
  const allAssignmentRules = useSelector(selectAllAssignmentRules)
  const dispatch = useAppDispatch()
  const saveSeq = useRef(0)
  const saveInFlight = useRef<Promise<void> | null>(null)
  const lastPersisted = useRef<IAutoReviewSettingsInput>({
    enabled: false,
    leadTypes: [],
  })

  useEffect(() => {
    let isMounted = true

    const loadSettings = async () => {
      const result = await dispatch(getAutoReviewSettings())

      if (!isMounted) return

      if (getAutoReviewSettings.fulfilled.match(result)) {
        setAutoReviewEnabled(result.payload.enabled)
        setSelectedTypes(result.payload.leadTypes)
        setRequiredAdditionalFields(
          result.payload.requiredAdditionalFields ?? [],
        )
        lastPersisted.current = {
          enabled: result.payload.enabled,
          leadTypes: result.payload.leadTypes,
        }
      } else {
        setLoadError(true)
        toast.error('Unable to load Pending Lead settings.')
      }

      setSettingsLoading(false)
    }

    const loadOptions = async () => {
      const result = await dispatch(getAdditionalRequiredFieldOptions())

      if (!isMounted) return

      if (getAdditionalRequiredFieldOptions.fulfilled.match(result)) {
        setFieldOptions(sortFieldOptions([...result.payload]))
      } else {
        toast.error('Unable to load the required field options.')
      }

      setOptionsLoading(false)
    }

    loadSettings()
    loadOptions()

    return () => {
      isMounted = false
    }
  }, [dispatch])

  const leadTypes: IOption[] = useMemo(() => {
    // Manual is excluded here only: the assignment-rule builder can't create
    // a rule for it, so the warning banner could never clear for that type.
    let LeadTypeData = (LeadTypeOptions ?? []).filter(
      (option) => option.value !== 'Manual',
    )

    if (enabledServiceBot) {
      LeadTypeData = [...LeadTypeData, ...leadTypeServiceBotOption]
    }

    return [...LeadTypeData, ...leadTypeImportOption]
  }, [enabledServiceBot])

  const visibleSelectedTypes = useMemo(() => {
    const visibleValues = new Set(
      leadTypes.map((option) => String(option.value)),
    )

    return selectedTypes.filter((type) => visibleValues.has(type))
  }, [leadTypes, selectedTypes])

  const leadTypeAssignmentRules = useMemo(() => {
    const selectedValues = new Set(
      visibleSelectedTypes.map((type) => type.toLowerCase()),
    )

    return allAssignmentRules.reduce<LeadTypeAssignmentRule[]>(
      (acc, rule: AssignmentRuleDTO) => {
        if (!rule.enabled) return acc

        const types = new Set<string>()

        rule.assignmentRuleConditions?.forEach((condition) => {
          if (condition.attribute !== LEAD_TYPE_ATTRIBUTE) return

          condition.assignmentRuleConditionValues?.forEach((conditionValue) => {
            if (conditionValue?.value == null) return

            const value = String(conditionValue.value)
            if (!selectedValues.has(value.toLowerCase())) return

            types.add(value)
          })
        })

        if (types.size === 0) return acc

        acc.push({
          assignmentRuleId: rule.id,
          assignmentRuleName: rule.name,
          types: Array.from(types),
        })

        return acc
      },
      [],
    )
  }, [allAssignmentRules, visibleSelectedTypes])

  const leadTypesWithoutRule = useMemo(() => {
    // Mirrors the server's GetCoveredLeadTypes: an enabled rule covers a type
    // only through its LeadType conditions (ANDed, so intersected). "None"
    // covers every assignable type it doesn't list; any other match type
    // covers the types it lists. Rules without a LeadType condition cover none.
    const assignableValues = leadTypes.map((option) =>
      String(option.value).toLowerCase(),
    )
    const coveredLeadTypeValues = new Set<string>()

    allAssignmentRules.forEach((rule) => {
      if (!rule.enabled) return

      const selections: Set<string>[] = []

      rule.assignmentRuleConditions?.forEach((condition) => {
        if (condition.attribute?.toLowerCase() !== LEAD_TYPE_ATTRIBUTE_LOWER)
          return

        const listed = new Set(
          (condition.assignmentRuleConditionValues ?? [])
            .filter((conditionValue) => conditionValue?.value != null)
            .map((conditionValue) =>
              String(conditionValue.value).trim().toLowerCase(),
            ),
        )
        const selected = new Set(
          assignableValues.filter((value) =>
            condition.matchType?.toLowerCase() === 'none'
              ? !listed.has(value)
              : listed.has(value),
          ),
        )

        selections.push(selected)
      })

      if (selections.length === 0) return

      assignableValues
        .filter((value) => selections.every((selected) => selected.has(value)))
        .forEach((value) => coveredLeadTypeValues.add(value))
    })

    // Import isn't offered as an auto-review type, so a stale Import selection
    // must not raise a warning the user has no checkbox to clear.
    return leadTypes.filter(
      (leadType) =>
        !importLeadTypeValues.has(String(leadType.value)) &&
        selectedTypes.includes(String(leadType.value) as LeadType) &&
        !coveredLeadTypeValues.has(String(leadType.value).toLowerCase()),
    )
  }, [allAssignmentRules, leadTypes, selectedTypes])

  const resyncRequiredFields = useCallback(async () => {
    const result = await dispatch(getAutoReviewSettings())

    if (getAutoReviewSettings.fulfilled.match(result)) {
      setRequiredAdditionalFields(result.payload.requiredAdditionalFields ?? [])
    }
  }, [dispatch])

  const save = useCallback(
    (next: IAutoReviewSettingsInput) => {
      setAutoReviewEnabled(next.enabled)
      setSelectedTypes(next.leadTypes)
      const seq = ++saveSeq.current

      // Chained off the previous save so PUTs hit the server strictly in
      // invocation order — never more than one in flight — otherwise an
      // older request could complete after a newer one and leave the
      // server holding stale settings even though the UI shows the latest.
      const run = async () => {
        const result = await dispatch(updateAutoReviewSettings(next))

        if (updateAutoReviewSettings.fulfilled.match(result)) {
          lastPersisted.current = next
          // The PUT response is the server's source of truth for the field
          // list, so apply it to keep the list from drifting.
          const { requiredAdditionalFields: persisted } = result.payload
          if (persisted) setRequiredAdditionalFields(persisted)
          return
        }

        // Only roll back to the last known-good server state if no newer
        // save has been requested since; a newer save already owns the
        // UI's optimistic state and will decide the final outcome.
        if (seq === saveSeq.current) {
          setAutoReviewEnabled(lastPersisted.current.enabled)
          setSelectedTypes(lastPersisted.current.leadTypes)
        }
        toast.error('Unable to save Pending Lead settings.')
      }

      const previous = saveInFlight.current
      const chained = previous ? previous.then(run, run) : run()
      saveInFlight.current = chained.finally(() => {
        if (saveInFlight.current === chained) {
          saveInFlight.current = null
        }
      })
      return chained
    },
    [dispatch],
  )

  return {
    autoReviewEnabled,
    selectedTypes,
    visibleSelectedTypes,
    leadTypes,
    requiredAdditionalFields,
    setRequiredAdditionalFields,
    fieldOptions,
    resyncRequiredFields,
    loading: assignmentRulesLoading || settingsLoading || optionsLoading,
    loadError,
    leadTypeAssignmentRules,
    leadTypesWithoutRule,
    save,
  }
}
